import { FormEvent, useContext, useState } from 'react';
import { EmpresasContext } from '../../context/EmpresasContext';
import { Autocomplete, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, Button, Skeleton } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import PasswordIcon from '@mui/icons-material/Password';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import InfoIcon from '@mui/icons-material/Info';
import { toast } from 'react-toastify';
import Empresas from '../../services/Empresas';
import AttToken from '../../services/AttToken';
import { CompanyAuthPolicy, IdentityProviderPolicy, UpdateCompanyAuthPolicyRequest, UpdateIdentityProviderPolicy } from '../../types/Empresa/types';
import { useMutation, useQuery } from 'react-query';

const empresaService = new Empresas();

const defaultPolicy: CompanyAuthPolicy = {
  version: 1,
  enabledMethods: ['password'],
  ssoPilotEnabled: false,
  reauthenticationIntervalMinutes: 0,
  singleDeviceOnly: false,
  deviceLockDays: 0,
  identityProviders: {
    google: { enabled: false, allowedDomains: [], redirectUris: [] },
    microsoft: { enabled: false, allowedDomains: [], redirectUris: [] },
  },
};

type IdentityProviderForm = {
  clientId: string;
  tenantId: string;
  secretReference: string;
  allowedDomains: string;
  redirectUris: string;
  hasSecretReference: boolean;
};

function toIdentityProviderForm(provider?: IdentityProviderPolicy): IdentityProviderForm {
  return {
    clientId: provider?.clientId ?? '',
    tenantId: provider?.tenantId ?? '',
    // A API não devolve a referência; ela só pode ser informada novamente quando necessário.
    secretReference: '',
    allowedDomains: (provider?.allowedDomains ?? []).join('\n'),
    redirectUris: (provider?.redirectUris ?? []).join('\n'),
    hasSecretReference: provider?.hasSecretReference ?? false,
  };
}

function toUpdateIdentityProviderPolicy(provider: IdentityProviderForm, enabled: boolean, includeTenantId = true): UpdateIdentityProviderPolicy {
  const toList = (value: string) => value.split(/[\n,]/).map((item) => item.trim()).filter(Boolean);

  return {
    enabled,
    ...(provider.clientId.trim() ? { clientId: provider.clientId.trim() } : {}),
    ...(includeTenantId && provider.tenantId.trim() ? { tenantId: provider.tenantId.trim() } : {}),
    ...(provider.secretReference.trim() ? { secretReference: provider.secretReference.trim() } : {}),
    allowedDomains: toList(provider.allowedDomains),
    redirectUris: toList(provider.redirectUris),
  };
}

type ToggleValue = 'senha' | 'google' | 'microsoft';

function ToggleButtonGroupComponent({ toggleValue, handleChangeToggle }: { toggleValue: ToggleValue[]; handleChangeToggle: (event: React.MouseEvent<HTMLElement>, newValue: ToggleValue[] | null) => void }) {
  return (
    <>
      <Typography color='textDisabled' fontWeight='bold'>
        Métodos de autenticação:
      </Typography>
      <ToggleButtonGroup
        color='primary'
        value={toggleValue ?? []}
        onChange={handleChangeToggle}
        aria-label="Platform"
      >
        <ToggleButton value="senha">
          <PasswordIcon />&nbsp;Senha
        </ToggleButton>
        <ToggleButton value="google">
          <GoogleIcon />&nbsp;Google
        </ToggleButton>
        <ToggleButton value="microsoft">
          <MicrosoftIcon />&nbsp;Microsoft
        </ToggleButton>
      </ToggleButtonGroup>
    </>
  )
}

function ReautenticacaoInput({ authTime, handleAuthTimeChange }: { authTime: string; handleAuthTimeChange: (event: React.ChangeEvent<HTMLInputElement>) => void }) {
  return (
    <>
      <Typography color='textDisabled' fontWeight='bold'>
        Reautenticação:
      </Typography>
      <Stack direction="row" spacing={1} alignItems="flex-end" justifyContent="flex-start" width="100%">
        <TextField
          slotProps={{ htmlInput: { maxLength: 4 } }}
          value={authTime}
          label="Intervalo"
          onChange={handleAuthTimeChange}
          sx={{ width: '100px' }} />
        <Typography color='textDisabled' fontWeight='bold'>
          minutos
        </Typography>
        <Typography fontWeight='bold'>
          *Após o prazo, o usuário precisará autenticar novamente.
        </Typography>
      </Stack>
    </>
  )
}

function Dispositivos({
  deviceLock,
  prazoRestricao,
  handlePrazoRestricaoChange,
  handleDeviceLockChange }: {
    deviceLock: boolean;
    prazoRestricao: string;
    handlePrazoRestricaoChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleDeviceLockChange: (event: React.MouseEvent<HTMLElement>) => void
  }) {
  return (
    <>
      <Typography color='textDisabled' fontWeight='bold'>
        Dispositivos:
      </Typography>
      <ToggleButton
        color='primary'
        value={deviceLock}
        selected={deviceLock ?? false}
        onChange={handleDeviceLockChange}
      >
        <PhoneAndroidIcon />&nbsp;Restrição de dispositivos
      </ToggleButton>
      {deviceLock && <Stack direction="row" spacing={1} alignItems="flex-end" justifyContent="flex-start" width="100%">
        <TextField
          slotProps={{ htmlInput: { maxLength: 3 } }}
          value={prazoRestricao}
          label="prazo"
          onChange={handlePrazoRestricaoChange}
          sx={{ width: '100px' }} />
        <Typography color='textDisabled' fontWeight='bold'>
          dias
        </Typography>
        <Tooltip title="prazo de restrição de dispositivos por usuário" placement="top">
          <InfoIcon />
        </Tooltip>
      </Stack>}
    </>
  );
}

function IdentityProviderFields({
  title,
  provider,
  onChange,
  showTenantId = true,
}: {
  title: string;
  provider: IdentityProviderForm;
  onChange: (field: keyof IdentityProviderForm, value: string | boolean) => void;
  showTenantId?: boolean;
}) {
  return (
    <Stack spacing={1.5} width="100%">
      <Typography color='textDisabled' fontWeight='bold'>{title}</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} width="100%">
        <TextField fullWidth label="Client ID" value={provider.clientId} onChange={(event) => onChange('clientId', event.target.value)} />
        {showTenantId && <TextField fullWidth label="Tenant ID" value={provider.tenantId} onChange={(event) => onChange('tenantId', event.target.value)} />}
      </Stack>
      <TextField
        fullWidth
        label="Referência do segredo"
        value={provider.secretReference}
        onChange={(event) => onChange('secretReference', event.target.value)}
        helperText={provider.hasSecretReference ? 'Há uma referência de segredo cadastrada. Preencha apenas para substituí-la.' : 'Informe a referência usada pelo serviço para localizar o segredo.'}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} width="100%">
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Domínios permitidos"
          value={provider.allowedDomains}
          onChange={(event) => onChange('allowedDomains', event.target.value)}
          helperText="Um domínio por linha ou separado por vírgula."
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="URIs de redirecionamento"
          value={provider.redirectUris}
          onChange={(event) => onChange('redirectUris', event.target.value)}
          helperText="Uma URI por linha ou separada por vírgula."
        />
      </Stack>
    </Stack>
  );
}

export default function PoliticasAcesso() {
  const { empresas, onLoading } = useContext(EmpresasContext);
  const [empresaId, setEmpresaId] = useState<string>('');
  const [toggleValue, setToggleValue] = useState<ToggleValue[]>([]);
  const [authTime, setAuthTime] = useState<string>('');
  const [deviceLock, setDeviceLock] = useState<boolean>(false);
  const [prazoRestricao, setPrazoRestricao] = useState<string>('');
  const [ssoPilotEnabled, setSsoPilotEnabled] = useState(false);
  const [googleProvider, setGoogleProvider] = useState<IdentityProviderForm>(toIdentityProviderForm());
  const [microsoftProvider, setMicrosoftProvider] = useState<IdentityProviderForm>(toIdentityProviderForm());

  const { isLoading } = useQuery({
    queryKey: ['authPolicy', empresaId],
    queryFn: async () => {
      if (!empresaId) return null;
      const token = await AttToken();
      if (!token) throw new Error('Token não encontrado');
      const response = await empresaService.getAuthPolicy(token, empresaId);
      return response.authPolicy ?? defaultPolicy;
    },
    retry: false,
    refetchOnWindowFocus: false,
    enabled: empresaId.length > 0,
    onSuccess: (current) => {
      setToggleValue((current?.enabledMethods ?? []).map((m) => m === 'password' ? 'senha' : m) as ToggleValue[]);
      setAuthTime(String(current?.reauthenticationIntervalMinutes ?? 0));
      setDeviceLock(current?.singleDeviceOnly ?? false);
      setPrazoRestricao(String(current?.deviceLockDays ?? 0));
      setSsoPilotEnabled(current?.ssoPilotEnabled ?? false);
      setGoogleProvider(toIdentityProviderForm(current?.identityProviders.google));
      setMicrosoftProvider(toIdentityProviderForm(current?.identityProviders.microsoft));
    },
    onError: () => toast.error('Não foi possível carregar a política de autenticação'),
  })

  const {mutate, isLoading: isSaving} = useMutation({
    mutationFn: async () => {
      if (!empresaId) throw new Error('Empresa não selecionada');
      const token = await AttToken();
      if (!token) throw new Error('Token não encontrado');
      const newPolicy: UpdateCompanyAuthPolicyRequest = {
        version: 1,
        enabledMethods: toggleValue?.map((m) => m === 'senha' ? 'password' : m),
        ssoPilotEnabled,
        reauthenticationIntervalMinutes: Number(authTime),
        singleDeviceOnly: deviceLock,
        deviceLockDays: Number(prazoRestricao),
        identityProviders: {
          google: toUpdateIdentityProviderPolicy(googleProvider, toggleValue.includes('google'), false),
          microsoft: toUpdateIdentityProviderPolicy(microsoftProvider, toggleValue.includes('microsoft')),
        },
      };
      return await empresaService.updateAuthPolicy(token, empresaId, newPolicy);
    },
    onSuccess: (data) => {
      toast.success('Política de autenticação atualizada com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erro ao atualizar a política de autenticação');
    },
  });

  function handleSave(event: FormEvent) {
    event.preventDefault();
    mutate();
  }

  function handleChangeToggle(_event: React.MouseEvent<HTMLElement>, newValue: ToggleValue[] | null) {
    const selectedMethods = newValue ?? [];

    if (!selectedMethods.includes('google')) {
      setGoogleProvider(toIdentityProviderForm());
    }
    if (!selectedMethods.includes('microsoft')) {
      setMicrosoftProvider(toIdentityProviderForm());
    }

    setToggleValue(selectedMethods);
  }

  function handleAuthTimeChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    if (/^\d*$/.test(value)) {
      setAuthTime(value);
    }
  }

  function handleDeviceLockChange(_event: React.MouseEvent<HTMLElement>) {
    setDeviceLock(prev => !prev);
    if (!deviceLock) {
      setPrazoRestricao('');
    }
  }

  function handlePrazoRestricaoChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    const prazo = Number(value);
    if (value === '' || (/^[1-9]\d{0,2}$/.test(value) && prazo >= 1 && prazo <= 366)) {
      setPrazoRestricao(value);
    }
  }

  function handleProviderChange(
    setProvider: React.Dispatch<React.SetStateAction<IdentityProviderForm>>,
    field: keyof IdentityProviderForm,
    value: string | boolean,
  ) {
    setProvider((current) => ({ ...current, [field]: value }));
  }

  const empresasArray = empresas
    ?.filter((empresa) => empresa.status === 'ativo')
    ?.sort((a, b) => a.nome.localeCompare(b.nome))
    ?.map((empresa) => ({ ...empresa })) ?? [];

  return (
    <Stack direction="column" spacing={2} alignItems="flex-start" justifyContent="flex-start" width="100%">
      <Autocomplete
        fullWidth
        disablePortal
        value={empresas?.find((empresa) => empresa.id === empresaId) ?? null}
        loading={onLoading}
        disabled={onLoading}
        getOptionLabel={(option) => option.nome}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        options={empresasArray}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.nome}
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Empresa" />}
        onChange={(_eve, value, reason) => {
          setEmpresaId(value?.id ?? '');
          if (reason === 'clear') {
            // clearFields();
          }
        }}
      />
      {empresaId?.length > 0
        && <Stack component="form"
          onSubmit={handleSave}
          spacing={2} width="100%" alignItems="flex-start">
          {isLoading ? <Stack spacing={2} width="100%" alignItems="flex-start">
            <Stack spacing={1} width="100%">
              <Skeleton variant="text" width={190} height={24} />
              <Stack direction="row" spacing={0.5}>
                <Skeleton variant="rounded" width={98} height={40} />
                <Skeleton variant="rounded" width={108} height={40} />
                <Skeleton variant="rounded" width={125} height={40} />
              </Stack>
            </Stack>
            <Stack spacing={1} width="100%">
              <Skeleton variant="text" width={125} height={24} />
              <Stack direction="row" spacing={1} alignItems="center">
                <Skeleton variant="rounded" width={100} height={56} />
                <Skeleton variant="text" width={58} height={24} />
                <Skeleton variant="text" width={330} height={24} />
              </Stack>
            </Stack>
            <Stack spacing={1} width="100%">
              <Skeleton variant="text" width={100} height={24} />
              <Skeleton variant="rounded" width={235} height={40} />
            </Stack>
            <Skeleton variant="rounded" width={105} height={40} />
            <Skeleton variant="rounded" width={130} height={36} />
          </Stack> : <>
            <ToggleButtonGroupComponent
              toggleValue={toggleValue}
              handleChangeToggle={handleChangeToggle} />
            <ReautenticacaoInput
              authTime={authTime}
              handleAuthTimeChange={handleAuthTimeChange} />
            <Dispositivos
              deviceLock={deviceLock}
              prazoRestricao={prazoRestricao}
              handlePrazoRestricaoChange={handlePrazoRestricaoChange}
              handleDeviceLockChange={handleDeviceLockChange} />
            <ToggleButton selected={ssoPilotEnabled} onChange={() => setSsoPilotEnabled((v) => !v)} value="sso">
              Piloto SSO
            </ToggleButton>
            {toggleValue.includes('google') && (
              <IdentityProviderFields
                title="Google"
                provider={googleProvider}
                showTenantId={false}
                onChange={(field, value) => handleProviderChange(setGoogleProvider, field, value)}
              />
            )}
            {toggleValue.includes('microsoft') && (
              <IdentityProviderFields
                title="Microsoft"
                provider={microsoftProvider}
                onChange={(field, value) => handleProviderChange(setMicrosoftProvider, field, value)}
              />
            )}
            <Button variant="contained" type="submit" disabled={isSaving}>{isSaving ? 'Salvando...' : 'Salvar política'}</Button>
          </>}
        </Stack>}
    </Stack>
  )
}
